import { yupResolver } from '@hookform/resolvers/yup'
import { filter, find, forEach, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { CheckinSettings, Languages } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getSettingsCheckin,
  updateSettingsCheckin,
  uploadParentalConsent,
} from '../../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Card, Checkbox, Input, Button } from '@/components/ui'
import SelectField from '@/components/select-field'
import { Trash } from 'lucide-react'

interface ICheckinValues {
  id?: string
  checkinEnabled?: boolean
  freeCheckinDays?: number
  checkinInPersonTax?: number
  checkinTax?: number
  noGuardianMinAge?: number
  parentalConsent?: boolean
  parentalConsentFiles?: {
    id?: string
    locale: string
    fileLink: string
  }[]
  faceDetectionEnabled: boolean
}

interface CheckinProps {
  allLanguages: Languages[]
  hasEditPermission: boolean
}

export const Checkin: React.FC<CheckinProps> = (props: CheckinProps) => {
  const { allLanguages, hasEditPermission } = props
  const { editionId, festivalName } = useParams()
  const [fileName, setFileName] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [selectedCheckinLanguageIndex, setSelectedCheckinLanguageIndex] =
    useState<number | undefined>()
  const [checkInState, setCheckInState] = useState<CheckinSettings>()
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getSettingsCheckin()
        if (resp.status === 200) {
          setCheckInState(resp.data)
          setValue('id', resp.data.id)
          setValue('checkinEnabled', resp.data.checkinEnabled)
          setValue('freeCheckinDays', resp.data.freeCheckinDays)
          setValue('checkinInPersonTax', resp.data.checkinInPersonTax)
          setValue('checkinTax', resp.data.checkinTax)
          setValue('noGuardianMinAge', resp.data.noGuardianMinAge)
          setValue('parentalConsent', resp.data.parentalConsent)
          setTimeout(() => {
            setValue('parentalConsentFiles', resp.data.parentalConsentFiles)
          }, 100)
          setValue('faceDetectionEnabled', resp.data.faceDetectionEnabled)

          const name: string[] = []
          map(resp.data.parentalConsentFiles, (file, index) => {
            const url = new URL(file.fileLink)
            const i = url?.pathname?.lastIndexOf('/') + 1
            const fileName = url?.pathname?.substring(i)
            name[index] = fileName
          })
          setFileName(name)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const deleteCheckinLanguage = () => {
    removeParentalConsentLanguages(selectedCheckinLanguageIndex)
  }

  const schema = yup.object().shape({
    id: yup.string().optional(),
    checkinEnabled: yup.boolean().required(),
    freeCheckinDays: yup.number().optional(),
    checkinInPersonTax: yup.number().optional(),
    checkinTax: yup.number().optional(),
    noGuardianMinAge: yup.number().optional(),
    parentalConsent: yup.boolean().optional(),
    parentalConsentFiles: yup.array().of(
      yup.object().shape({
        id: yup.string().optional(),
        locale: yup.string().required(),
        fileLink: yup.string().required(),
      }),
    ),
    faceDetectionEnabled: yup.boolean().required(),
  })

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
    setValue,
    control,
  } = useForm<ICheckinValues>({
    mode: 'onChange',
    resolver: yupResolver<ICheckinValues>(schema),
    defaultValues: {},
  })

  const {
    fields: parentalConsentFields,
    append: appendParentalConsentLanguages,
    remove: removeParentalConsentLanguages,
  } = useFieldArray({
    control: control,
    name: 'parentalConsentFiles',
  })

  const parentalConsent = watch('parentalConsent')
  const parentalConsentFiles = watch('parentalConsentFiles')

  const submit = async (values: ICheckinValues) => {
    try {
      const { status } = await updateSettingsCheckin(values)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const defaultLanguage = () => {
    const response = {
      locale: 'en',
      name: '',
      id: '',
    }
    forEach(allLanguages, (lang) => {
      if (
        find(parentalConsentFiles, (cont) => lang.locale === cont?.locale) ===
        undefined
      ) {
        response.locale = lang.locale
        response.id = lang.id
        response.name = lang.name
        return
      }
    })
    return response
  }

  const handleCancel = async () => {
    if (checkInState) {
      setValue('id', checkInState.id)
      setValue('checkinEnabled', checkInState.checkinEnabled)
      setValue('freeCheckinDays', checkInState.freeCheckinDays)
      setValue('checkinInPersonTax', checkInState.checkinInPersonTax)
      setValue('checkinTax', checkInState.checkinTax)
      setValue('noGuardianMinAge', checkInState.noGuardianMinAge)
      setValue('parentalConsent', checkInState.parentalConsent)
      setTimeout(() => {
        setValue('parentalConsentFiles', checkInState.parentalConsentFiles)
      }, 100)
      setValue('faceDetectionEnabled', checkInState.faceDetectionEnabled)
    }
  }

  return (
    <div>
      <div>
        <h4 className="text-title-s text-accent-1 mb-4">Checkin</h4>
      </div>
      <Card className="p-4 mt-4">
        <form id="settings-checkin-form" onSubmit={handleSubmit(submit)}>
          <div className="flex flex-col gap-2 space-y-2">
            <FormGroup label="Free Checkin (Number of Days)">
              <Input
                isInvalid={!!errors?.freeCheckinDays?.message}
                {...register('freeCheckinDays')}
                type="number"
                placeholder="Free Checkin (Number of Days)"
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Checkin Online Tax (Default Currency)">
              <Input
                prefix="RON"
                isInvalid={!!errors?.checkinTax?.message}
                {...register('checkinTax')}
                type="number"
                min={0}
                placeholder="Checkin Online Tax (Default Currency)"
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Checkin In Person Tax (Default Currency)">
              {/* change this when we have currency reimplemented*/}
              <Input
                prefix="RON"
                isInvalid={!!errors?.checkinInPersonTax?.message}
                {...register('checkinInPersonTax')}
                type="number"
                min={0}
                placeholder="Checkin In Person Tax (Default Currency)"
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="No Guardian Needed Minimum Age">
              <Input
                isInvalid={!!errors?.noGuardianMinAge?.message}
                {...register('noGuardianMinAge')}
                type="number"
                placeholder="No Guardian Needed Minimum Age"
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup
              label="Enable Check-in"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Checkbox
                isInvalid={!!errors?.checkinEnabled?.message}
                {...register('checkinEnabled')}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup
              label="Enable Face Detection"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Checkbox
                isInvalid={!!errors?.faceDetectionEnabled?.message}
                {...register('faceDetectionEnabled')}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup
              label="Parental Consent"
              direction="row"
              labelClassName="whitespace-nowrap"
            >
              <Checkbox
                isInvalid={!!errors?.parentalConsent?.message}
                {...register('parentalConsent')}
                onCheckedChange={(checked) => {
                  setValue('parentalConsent', checked === true)
                  if (checked === true) {
                    appendParentalConsentLanguages({
                      locale: defaultLanguage().locale,
                      fileLink: '',
                    })
                  } else {
                    removeParentalConsentLanguages()
                  }
                }}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            {parentalConsent && (
              <>
                {map(parentalConsentFields, (field, index) => (
                  <Card key={field.id} className="p-4 space-y-2">
                    <FormGroup label="Language">
                      <Controller
                        name={`parentalConsentFiles.${index}.locale`}
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            outline
                            options={map(allLanguages, (lang) => ({
                              value: lang.locale,
                              label: lang.name,
                            }))}
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={!hasEditPermission}
                            error={
                              errors?.parentalConsentFiles?.[index]?.locale
                                ?.message
                            }
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup label="Upload File (.PDF)">
                      {parentalConsentFiles?.[index] === undefined ||
                      parentalConsentFiles?.[index].fileLink === '' ? (
                        <Input
                          multiple={false}
                          accept="application/pdf"
                          disabled={!hasEditPermission}
                          isInvalid={
                            !!errors?.parentalConsentFiles?.[index]?.fileLink
                          }
                          name={`parentalConsent.${index}.file`}
                          onChange={async (
                            e: ChangeEvent<HTMLInputElement>,
                          ) => {
                            const file = e?.target?.files?.[0]
                            const locale = watch('parentalConsentFiles')?.[
                              index
                            ]?.locale
                            if (!locale) return
                            if (file) {
                              try {
                                const { data } = await uploadParentalConsent(
                                  file,
                                  locale,
                                )
                                if (data.fileUrl) {
                                  setValue(
                                    `parentalConsentFiles.${index}.fileLink`,
                                    data.fileUrl,
                                  )
                                  const temp = [...fileName]
                                  temp[index] = file.name
                                  setFileName(temp)
                                }
                              } catch (err) {
                                console.log(err)
                              }
                            }
                          }}
                          type="file"
                        />
                      ) : (
                        <div className="w-full rounded-md border border-monochrome-120 bg-monochrome-3-60">
                          <div className="flex items-center justify-between gap-3 p-2">
                            <span className="w-full text-accent-1 font-normal">
                              {fileName}
                            </span>
                            <Trash
                              className="me-2 cursor-pointer"
                              size={16}
                              onClick={() => {
                                if (!hasEditPermission) return
                                const temp = [...fileName]
                                temp[index] = ''
                                setFileName(temp)
                                setValue(
                                  `parentalConsentFiles.${index}.fileLink`,
                                  '',
                                )
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </FormGroup>
                    <div className="flex justify-end mt-2">
                      <Button
                        variant="destructive"
                        type="button"
                        disabled={!hasEditPermission}
                        onClick={() => {
                          setDeleteConfirmationModal(true)
                          setSelectedCheckinLanguageIndex(index)
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </Card>
                ))}
              </>
            )}
            {parentalConsent &&
              parentalConsentFields.length < allLanguages.length && (
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full mt-2"
                  disabled={!hasEditPermission}
                  onClick={() => {
                    const selected = watch('parentalConsentFiles')?.[0]?.locale

                    const otherLanguage = filter(allLanguages, (lang) => {
                      return lang.locale !== selected
                    })

                    appendParentalConsentLanguages({
                      locale: otherLanguage[0].locale || '',
                      fileLink: '',
                    })
                  }}
                >
                  Add new
                </Button>
              )}
          </div>
        </form>

        <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 mt-4">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-checkin-form"
            type="submit"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Language'}
        deleteFunc={deleteCheckinLanguage}
        onClose={() => setSelectedCheckinLanguageIndex(undefined)}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </div>
  )
}
